import { FilterCriteria } from '@ampsec/platform-client';
import { useMemo } from 'react';
import useSWR from 'swr';

export type ApiDataResponse<RenderT> = {
    data: RenderT[] | RenderT | null | undefined;
    error: string;
    isLoading: boolean;
    refresh: () => void;
};

export const useApiData = <DataT, RenderT>(
    cacheKey: string,
    fetcher: any,
    // eslint-disable-next-line no-unused-vars
    transformer: (dataItem: DataT) => RenderT,
    filter?: FilterCriteria,
): ApiDataResponse<RenderT> => {
    const updatedFilter: FilterCriteria = { ...filter };
    const { data: apiData, error, isLoading, mutate } = useSWR<DataT[] | DataT>([cacheKey, updatedFilter], () =>
        fetcher(cacheKey, updatedFilter),
    );
    /** Derive from SWR in render so `data` never lags `isLoading` by a frame (fixes gated fetches that depend on this data). */
    const data = useMemo(() => {
        if (!apiData) {
            return [] as unknown as RenderT | RenderT[];
        }

        if (!Array.isArray(apiData)) {
            return transformer(apiData);
        }

        return apiData.map(transformer);
    }, [apiData]);
    const refresh = () => {
        mutate();
    };
    return {
        data,
        error,
        isLoading,
        refresh,
    };
};
